import { FFmpeg } from '@ffmpeg/ffmpeg'
import { fetchFile } from '@ffmpeg/util'

let ffmpeg = null

// Initialize FFmpeg instance
const initializeFFmpeg = async (onProgress) => {
  if (ffmpeg) return ffmpeg

  ffmpeg = new FFmpeg()

  if (onProgress) {
    ffmpeg.on('log', ({ message }) => {
      console.log('FFmpeg:', message)
    })

    ffmpeg.on('progress', ({ progress }) => {
      onProgress(progress * 100)
    })
  }

  try {
    // Load FFmpeg - it will automatically fetch the correct core files
    await ffmpeg.load()

    console.log('✅ FFmpeg loaded successfully')
    return ffmpeg
  } catch (error) {
    console.error('❌ FFmpeg loading failed:', error)
    throw new Error('Failed to initialize video processing engine')
  }
}

// Detect video codec using file extension (fast fallback)
export const detectVideoCodec = async (file) => {
  try {
    console.log('🔍 TESTING REAL BROWSER PLAYBACK for:', file.name)

    // Create video element and test EXACTLY like real playback
    const videoElement = document.createElement('video')
    videoElement.preload = 'metadata'
    videoElement.muted = true // Prevent audio issues

    let videoUrl
    if (file instanceof File) {
      videoUrl = URL.createObjectURL(file)
    } else {
      videoUrl = file // Already a URL from playback system
    }

    return new Promise((resolve, reject) => {
      let timeoutId
      let resolved = false

      const cleanup = () => {
        if (file instanceof File) {
          URL.revokeObjectURL(videoUrl)
        }
        if (timeoutId) clearTimeout(timeoutId)
      }

      const resolveOnce = (result) => {
        if (resolved) return
        resolved = true
        cleanup()
        resolve(result)
      }

      // Test multiple events to catch playback issues
      let canPlayEvent = false
      let loadedDataEvent = false
      let errorEvent = false

      videoElement.oncanplay = () => {
        canPlayEvent = true
        console.log('✅ Video canplay event fired')
      }

      videoElement.onloadeddata = () => {
        loadedDataEvent = true
        console.log('✅ Video loadeddata event fired')
      }

      videoElement.onloadedmetadata = () => {
        const width = videoElement.videoWidth || 0
        const height = videoElement.videoHeight || 0
        const duration = videoElement.duration || 0

        console.log(`📊 Video metadata: ${width}x${height}, duration: ${duration}s`)

        // Wait a bit more to see if canplay fires
        setTimeout(() => {
          const reallyCanPlay = canPlayEvent && loadedDataEvent && width > 0 && height > 0 && duration > 0

          const extension = getFileExtension(file.name || 'unknown.mp4').toLowerCase()
          let videoCodec = 'unknown'
          let audioCodec = 'unknown'

          if (reallyCanPlay) {
            // Browser can actually play it
            if (extension === '.mp4') {
              videoCodec = 'h264'
              audioCodec = 'aac'
            } else if (extension === '.webm') {
              videoCodec = 'vp8'
              audioCodec = 'opus'
            } else if (extension === '.mov') {
              videoCodec = 'h264'
              audioCodec = 'aac'
            } else {
              videoCodec = 'h264' // Assume compatible if playing
              audioCodec = 'aac'
            }
          } else {
            // Browser struggles with it
            if (extension === '.mp4') {
              videoCodec = 'h265' // Most likely culprit for MP4 issues
              audioCodec = 'aac'
            } else if (extension === '.mkv') {
              videoCodec = 'h265'
              audioCodec = 'ac3'
            } else {
              videoCodec = 'unknown'
              audioCodec = 'unknown'
            }
          }

          console.log(`📊 REAL BROWSER TEST: ${reallyCanPlay ? '✅ WILL PLAY' : '❌ WILL FAIL'} - ${videoCodec}/${audioCodec}`)

          resolveOnce({
            videoCodec,
            audioCodec,
            width: width || 1920,
            height: height || 1080,
            duration: duration || 0,
            bitRate: 0,
            hasVideo: width > 0,
            hasAudio: true,
            browserCanPlay: reallyCanPlay,
            canPlayEvent,
            loadedDataEvent,
            errorEvent
          })
        }, 1000) // Wait 1 second for events to settle
      }

      videoElement.onerror = (e) => {
        errorEvent = true
        console.log('❌ Video error event:', e)

        const extension = getFileExtension(file.name || 'unknown.mp4').toLowerCase()
        let videoCodec = 'h265' // Assume incompatible codec
        let audioCodec = 'unknown'

        if (extension === '.mp4') {
          videoCodec = 'h265'
          audioCodec = 'aac'
        } else if (extension === '.mkv') {
          videoCodec = 'h265'
          audioCodec = 'ac3'
        }

        console.log(`📊 BROWSER ERROR - INCOMPATIBLE: ${videoCodec}/${audioCodec}`)

        resolveOnce({
          videoCodec,
          audioCodec,
          width: 1920,
          height: 1080,
          duration: 0,
          bitRate: 0,
          hasVideo: false,
          hasAudio: false,
          browserCanPlay: false,
          canPlayEvent: false,
          loadedDataEvent: false,
          errorEvent: true
        })
      }

      // Timeout after 15 seconds
      timeoutId = setTimeout(() => {
        console.warn('⚠️ Video loading timed out - assuming incompatible')

        resolveOnce({
          videoCodec: 'h265', // Assume timeout means incompatible
          audioCodec: 'unknown',
          width: 1920,
          height: 1080,
          duration: 0,
          bitRate: 0,
          hasVideo: false,
          hasAudio: false,
          browserCanPlay: false,
          canPlayEvent: false,
          loadedDataEvent: false,
          errorEvent: false
        })
      }, 15000)

      // Start the test
      videoElement.src = videoUrl
      videoElement.load()
    })

  } catch (error) {
    console.error('❌ Browser compatibility test crashed:', error)
    throw new Error('Failed to test video compatibility')
  }
}

// Check if codec is browser-compatible
export const isCodecBrowserCompatible = (codecInfo) => {
  const { videoCodec, audioCodec, browserCanPlay } = codecInfo

  // NEW: Use actual browser test result with ACTIONABLE RECOMMENDATIONS
  if (typeof browserCanPlay === 'boolean') {
    const compatibility = {
      videoCompatible: browserCanPlay,
      audioCompatible: browserCanPlay,
      overall: browserCanPlay
    }

    if (browserCanPlay) {
      compatibility.recommendation = '✅ WILL PLAY: Browser successfully loaded this video. Safe to upload.'
      compatibility.action = 'Go ahead and upload - this video will play perfectly.'
    } else {
      // Specific recommendations based on likely codec issues
      if (videoCodec === 'h265') {
        compatibility.recommendation = '❌ WILL FAIL: H.265 codec not supported in most browsers.'
        compatibility.action = 'SOLUTION: Use HandBrake or similar to convert to H.264 before uploading.'
      } else if (videoCodec === 'unknown') {
        compatibility.recommendation = '❌ WILL FAIL: Unknown or incompatible video codec.'
        compatibility.action = 'SOLUTION: Convert to MP4 with H.264 video + AAC audio using HandBrake.'
      } else {
        compatibility.recommendation = '❌ WILL FAIL: This video format is not browser-compatible.'
        compatibility.action = 'SOLUTION: Convert to standard MP4 (H.264/AAC) format before uploading.'
      }

      compatibility.techDetails = `Detected: ${videoCodec.toUpperCase()}/${audioCodec.toUpperCase()}`
      compatibility.convertSettings = 'Recommended: MP4 container, H.264 video codec, AAC audio codec'
    }

    return compatibility
  }

  // Fallback to codec-based detection
  // Browser-compatible video codecs
  const compatibleVideoCodecs = [
    'h264',      // H.264/AVC - Most compatible
    'vp8',       // WebM VP8
    'vp9',       // WebM VP9
    'av01'       // AV1 (modern browsers)
  ]

  // Browser-compatible audio codecs
  const compatibleAudioCodecs = [
    'aac',       // AAC - Most compatible
    'mp3',       // MP3
    'opus',      // Opus (WebM)
    'vorbis'     // Vorbis (WebM)
  ]

  const videoCompatible = compatibleVideoCodecs.some(codec =>
    videoCodec.toLowerCase().includes(codec)
  )

  const audioCompatible = compatibleAudioCodecs.some(codec =>
    audioCodec.toLowerCase().includes(codec)
  )

  return {
    isCompatible: videoCompatible && audioCompatible,
    videoCompatible,
    audioCompatible,
    recommendation: getCompatibilityRecommendation(videoCodec, audioCodec)
  }
}

// Convert video to browser-compatible format
export const convertToBrowserCompatible = async (file, onProgress) => {
  try {
    const ffmpegInstance = await initializeFFmpeg(onProgress)

    const inputName = 'input' + getFileExtension(file.name)
    const outputName = 'output.mp4'

    // Write input file
    await ffmpegInstance.writeFile(inputName, await fetchFile(file))

    // Convert to H.264 + AAC in MP4 container (most compatible)
    await ffmpegInstance.exec([
      '-i', inputName,
      '-c:v', 'libx264',          // H.264 video codec
      '-preset', 'medium',        // Balance quality/speed
      '-crf', '23',              // Good quality
      '-c:a', 'aac',             // AAC audio codec
      '-b:a', '128k',            // Audio bitrate
      '-movflags', '+faststart', // Optimize for streaming
      '-f', 'mp4',               // MP4 container
      outputName
    ])

    // Read converted file
    const convertedData = await ffmpegInstance.readFile(outputName)

    // Clean up
    await ffmpegInstance.deleteFile(inputName)
    await ffmpegInstance.deleteFile(outputName)

    // Create new File object
    const convertedBlob = new Blob([convertedData], { type: 'video/mp4' })
    const originalName = file.name.replace(/\.[^/.]+$/, '')
    const convertedFile = new File([convertedBlob], `${originalName}_converted.mp4`, {
      type: 'video/mp4'
    })

    return convertedFile

  } catch (error) {
    console.error('Video conversion failed:', error)
    throw new Error('Failed to convert video to compatible format')
  }
}

// Helper functions
const getFileExtension = (filename) => {
  const lastDot = filename.lastIndexOf('.')
  return lastDot !== -1 ? filename.substring(lastDot) : ''
}

const getCompatibilityRecommendation = (videoCodec, audioCodec) => {
  if (videoCodec.toLowerCase().includes('h264') && audioCodec.toLowerCase().includes('aac')) {
    return 'Excellent - Should work in all browsers'
  }

  if (videoCodec.toLowerCase().includes('vp8') || videoCodec.toLowerCase().includes('vp9')) {
    return 'Good - Works in most modern browsers'
  }

  return 'Conversion recommended - May not play in all browsers'
}

// Test browser video support
export const testBrowserVideoSupport = () => {
  const video = document.createElement('video')

  return {
    h264_aac: video.canPlayType('video/mp4; codecs="avc1.42E01E, mp4a.40.2"') !== '',
    h264_baseline: video.canPlayType('video/mp4; codecs="avc1.42E01E"') !== '',
    webm_vp8: video.canPlayType('video/webm; codecs="vp8"') !== '',
    webm_vp9: video.canPlayType('video/webm; codecs="vp9"') !== '',
    webm_av1: video.canPlayType('video/webm; codecs="av01.0.M.08"') !== ''
  }
}